import { createFormatForDescription as cffd } from '../createFormatForDescription'
import explainAmount from '../explain/explainAmount'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import OfferCreateInstructions from '../types/instructions/OfferCreateInstructions'
import TransactionType from '../types/TransactionType'
import CurrencyAmount from '../types/util/CurrencyAmount'
import { parseDate } from '../util'

/**
 * Annotation for an OfferCreate transaction.
 *
 * @property type - The transaction type.
 * @property expiration - An explanation of the expiration.
 * @property offerSequence - An explanation of the offerSequence.
 * @property takerGets - An explanation of the takerGets.
 * @property takerPays - An explanation of the takerPays.
 */
export interface OfferCreateAnnotation {
  readonly type: TransactionType.OfferCreate
  readonly expiration?: Explanation<number>
  readonly offerSequence?: Explanation<number>
  readonly takerGets: Explanation<CurrencyAmount>
  readonly takerPays: Explanation<CurrencyAmount>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: OfferCreateInstructions,
): OfferCreateAnnotation {
  const expiration = explainOptional(
    transactionInstructions.Expiration,
    (date) => `This offer lasts until ${cffd(parseDate(date), 'expiration')}`,
  )

  const offerSequence = explainOptional(
    transactionInstructions.OfferSequence,
    (sequenceNum) =>
      `Cancel the OfferCreate transaction with sequence number ${cffd(
        sequenceNum.toString(),
        'offerSequence',
      )}.`,
  )

  const takerGets = explain(
    transactionInstructions.TakerGets,
    `The taker gets ${explainAmount(transactionInstructions.TakerGets)}.`,
  )

  const takerPays = explain(
    transactionInstructions.TakerPays,
    `The taker pays ${explainAmount(transactionInstructions.TakerPays)}.`,
  )

  return {
    type: TransactionType.OfferCreate,
    ...(expiration && { expiration }),
    ...(offerSequence && { offerSequence }),
    takerGets,
    takerPays,
  }
}
